// src/components/strategies/StrategyChartComponent.jsx

import { useMemo } from 'react';
import Chart from 'react-apexcharts';

// =================================================================================
// КОНСТАНТЫ
// =================================================================================
const NAVIGATOR_DOWNSAMPLE_TARGET = 500;    
const INITIAL_VISIBLE_POINTS = 1000;        
const Y_AXIS_PADDING = 0.5;                 

// =================================================================================
// ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ И ФУНКЦИИ
// =================================================================================

const ChartLegend = () => (
  <div className="w-fit flex flex-wrap gap-2 md:gap-3 bg-slate-900/50 p-2 rounded-lg text-[10px] md:text-xs text-gray-300 whitespace-nowrap">
    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#00B0FF]"></span><span>Entry</span></div>
    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#7E57C2]"></span><span>Exit</span></div>
    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#FFC107]"></span><span>Open</span></div>
  </div>
);

const ChartLoadingState = ({ className }) => (
  <div className={`flex h-full items-center justify-center rounded-xl border border-slate-700/70 bg-slate-800/60 ${className}`}>
    <svg
      className="chart-loading-wave w-72 h-28 md:w-96 md:h-32"
      viewBox="0 0 520 160"
      role="presentation"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="waveGradient" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#1E3A8A" />
          <stop offset="50%" stopColor="#38BDF8" />
          <stop offset="100%" stopColor="#1E40AF" />
        </linearGradient>
        <linearGradient id="waveGradientBright" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#60A5FA" />
          <stop offset="50%" stopColor="#7DD3FC" />
          <stop offset="100%" stopColor="#60A5FA" />
        </linearGradient>
        <linearGradient id="waveFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(56,189,248,0.22)" />
          <stop offset="100%" stopColor="rgba(30,58,138,0.02)" />
        </linearGradient>
      </defs>

      <g className="chart-loading-grid">
        <line x1="20" y1="30" x2="500" y2="30" />
        <line x1="20" y1="70" x2="500" y2="70" />
        <line x1="20" y1="110" x2="500" y2="110" />
        <line x1="20" y1="150" x2="500" y2="150" />
        <line x1="20" y1="20" x2="20" y2="150" />
        <line x1="120" y1="20" x2="120" y2="150" />
        <line x1="220" y1="20" x2="220" y2="150" />
        <line x1="320" y1="20" x2="320" y2="150" />
        <line x1="420" y1="20" x2="420" y2="150" />
        <line x1="500" y1="20" x2="500" y2="150" />
      </g>

      <g className="chart-loading-chart">
        <path
          className="chart-area"
          d="M20 120 L20 96 C50 78, 80 60, 110 72 C140 84, 170 120, 200 102 C230 84, 260 46, 290 60 C320 74, 350 126, 380 108 C410 90, 440 62, 470 70 C495 76, 505 88, 500 98 L500 150 L20 150 Z"
          fill="url(#waveFill)"
        />
        <path
          className="chart-line"
          d="M20 96 C50 78, 80 60, 110 72 C140 84, 170 120, 200 102 C230 84, 260 46, 290 60 C320 74, 350 126, 380 108 C410 90, 440 62, 470 70 C495 76, 505 88, 500 98"
          fill="none"
          stroke="url(#waveGradient)"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          className="chart-line-secondary"
          d="M20 112 C55 100, 85 86, 120 92 C150 98, 180 124, 210 114 C240 104, 270 78, 300 86 C330 94, 360 122, 390 112 C420 102, 455 84, 500 94"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          className="chart-line-highlight"
          d="M20 96 C50 78, 80 60, 110 72 C140 84, 170 120, 200 102 C230 84, 260 46, 290 60 C320 74, 350 126, 380 108 C410 90, 440 62, 470 70 C495 76, 505 88, 500 98"
          fill="none"
          stroke="url(#waveGradientBright)"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <g className="chart-dots">
          <circle cx="110" cy="72" r="3.5" />
          <circle cx="200" cy="102" r="3.5" />
          <circle cx="290" cy="60" r="3.5" />
          <circle cx="380" cy="108" r="3.5" />
          <circle cx="470" cy="70" r="3.5" />
        </g>
      </g>
    </svg>
  </div>
);

const createMarker = (timestamp, yValue, color) => ({
  x: timestamp,
  y: yValue,
  seriesIndex: 0,
  marker: {
    size: 5,
    fillColor: color,
    strokeColor: '#1F2937',
    strokeWidth: 1.5,
    radius: 2,
  },
});

// =================================================================================
// ОСНОВНОЙ КОМПОНЕНТ ГРАФИКА
// =================================================================================

const StrategyChartComponent = ({ data, status, strategyParams, className }) => {
  
  const commonOptions = useMemo(() => ({
    theme: { mode: 'dark' },
    chart: { 
        animations: { enabled: false }, 
        width: '100%', 
        height: '100%',
        foreColor: '#9CA3AF'
    },
    stroke: { width: 1.5, curve: 'straight' },
    grid: { borderColor: '#444', strokeDashArray: 2 },
    tooltip: { theme: 'dark', x: { format: 'dd MMM yyyy HH:mm:ss' } },
    xaxis: { type: 'datetime', labels: { style: { colors: '#9CA3AF' } } },
    dataLabels: { enabled: false },
  }), []);

  // Основная логика подготовки данных
  const { 
    series, 
    navigatorSeries, 
    annotations, 
    initialXAxisRange, 
    yAxisRange,
    hasData 
  } = useMemo(() => {
    // 1. Проверка на наличие данных
    if (!data || !data.zScoresData || data.zScoresData.length === 0) {
      return { 
          series: [], navigatorSeries: [], annotations: {}, 
          initialXAxisRange: { min: undefined, max: undefined }, 
          yAxisRange: { min: -5, max: 5 },
          hasData: false
      };
    }
    
    const zScoresData = data.zScoresData.map(d => [d.timestamp, d.value]).filter(d => d[1] !== undefined && d[1] !== null);
    
    // 2. Если после фильтрации данных нет
    if (zScoresData.length === 0) {
        return { 
            series: [], navigatorSeries: [], annotations: {}, 
            initialXAxisRange: { min: undefined, max: undefined }, 
            yAxisRange: { min: -5, max: 5 },
            hasData: false 
        };
    }

    const maxAbsY = Math.max(...zScoresData.map(d => Math.abs(d[1] || 0)));
    const fixedYRange = {
      min: -(maxAbsY + Y_AXIS_PADDING),
      max: maxAbsY + Y_AXIS_PADDING
    };

    const minX = zScoresData[0][0];
    const maxX = zScoresData[zScoresData.length - 1][0];

    // Вычисляем начальный зум (последние N точек)
    const initialMinX = zScoresData[Math.max(0, zScoresData.length - INITIAL_VISIBLE_POINTS)]?.[0];

    const initialRange = {
      min: initialMinX || minX,
      max: maxX
    };

    const navigatorData = zScoresData.length > NAVIGATOR_DOWNSAMPLE_TARGET ? downsampleLTTB(zScoresData, NAVIGATOR_DOWNSAMPLE_TARGET) : zScoresData;
    
    const points = [];
    (data.completedTrades_raw || []).forEach(trade => {
        if(trade.entries) trade.entries.forEach(entry => points.push(createMarker(entry.time, entry.z, '#00B0FF')));
        if(trade.exit_time) points.push(createMarker(trade.exit_time, trade.exit_z, '#7E57C2'));
    });
    if (data.openTrade?.entries) {
        data.openTrade.entries.forEach(entry => points.push(createMarker(entry.time, entry.z, '#FFC107')));
    }
    
    const levels = strategyParams?.level_entry || { level_entry_positive: [], level_entry_negative: [] };
    const yaxisAnnotations = [
      ...(levels.level_entry_positive || []).map(l => ({ y: l, borderColor: '#E91E63', strokeDashArray: 4 })),
      ...(levels.level_entry_negative || []).map(l => ({ y: l, borderColor: '#00E396', strokeDashArray: 4 }))
    ];

    return {
      series: [{ name: 'Z-Score', data: zScoresData }],
      navigatorSeries: [{ name: 'Z-Score', data: navigatorData }],
      annotations: { yaxis: yaxisAnnotations, points },
      initialXAxisRange: initialRange,
      yAxisRange: fixedYRange,
      hasData: true
    };
  }, [data, strategyParams]);

  // Генерируем уникальные ID для графиков, чтобы при смене стратегии ApexCharts пересоздавал инстансы
  const chartId = `chart-${strategyParams.symbol_a}-${strategyParams.symbol_b}-${strategyParams.timeframe}`;
  const mainChartId = `main-${chartId}`;
  const navChartId = `nav-${chartId}`;

  // Опции для основного графика
  const mainChartOptions = useMemo(() => ({
    ...commonOptions,
    chart: { 
      ...commonOptions.chart, 
      id: mainChartId, // Уникальный ID
      type: 'line', 
      background: 'transparent',
      toolbar: { 
        autoSelected: 'pan',
        tools: { download: false, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      },
      zoom: { enabled: true, type: 'x', autoScaleYaxis: false },
    },
    xaxis: { 
      type: 'datetime',
      min: initialXAxisRange.min,
      max: initialXAxisRange.max,
      labels: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      min: yAxisRange.min,
      max: yAxisRange.max,
      title: { text: 'Z-Score', style: { color: '#9CA3AF' } },
      labels: { formatter: (value) => (typeof value === 'number' ? value.toFixed(1) : value) }
    },
    annotations,
  }), [commonOptions, mainChartId, initialXAxisRange, yAxisRange, annotations]);

  // Опции для навигатора
  const navigatorChartOptions = useMemo(() => {
    // Важно: если range невалидный, не включаем selection, иначе ApexCharts падает
    const isSelectionValid = initialXAxisRange.min !== undefined && initialXAxisRange.max !== undefined && !isNaN(initialXAxisRange.min);

    return {
        ...commonOptions,
        chart: {
            ...commonOptions.chart,
            id: navChartId,
            type: 'area', 
            background: 'transparent',
            brush: { 
                enabled: true, 
                target: mainChartId // Ссылка на ID основного графика
            },
            selection: {
                enabled: true,
                xaxis: isSelectionValid ? {
                    min: initialXAxisRange.min,
                    max: initialXAxisRange.max
                } : undefined
            },
        },
        yaxis: { show: false },
        tooltip: { enabled: false },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 100] }}
    };
  }, [commonOptions, navChartId, mainChartId, initialXAxisRange]);

  const isLoading = status === 'Connecting' || status === 'Idle' || status.toLowerCase().includes('loading');
  if (isLoading) {
    return <ChartLoadingState className={className} />;
  }
  if (status === 'Error' || status === 'Closed') {
    return <div className={`flex items-center justify-center h-full bg-slate-800 rounded-xl text-red-400 ${className}`}><p>Connection failed.</p></div>;
  }
  
  // Если данных нет, не рендерим Chart вообще, чтобы избежать краша
  if (!hasData) {
      return <div className={`flex items-center justify-center h-full bg-slate-800 rounded-xl text-gray-500 ${className}`}><p>No data available for chart</p></div>;
  }

  return (
    <div className={`relative bg-slate-800 p-4 md:p-6 rounded-xl shadow-lg h-full flex flex-col ${className}`}>
      
      <div className="mb-4 shrink-0 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-bold text-white break-words">
            {`${strategyParams.symbol_a} / ${strategyParams.symbol_b}`}
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            {`Timeframe: ${strategyParams.timeframe}m | Correlation: ${(data?.correlation || 0).toFixed(4)}`}
          </p>
        </div>
        <div className="shrink-0">
           <ChartLegend />
        </div>
      </div>

      <div className="flex-1 min-h-0 w-full">
        {/* Используем key, чтобы при смене монеты полностью пересоздать компонент графика */}
        <Chart key={mainChartId} options={mainChartOptions} series={series} type="line" height="100%" width="100%" />
      </div>
      <div className="h-[100px] md:h-[130px] shrink-0 w-full mt-2">
        <Chart key={navChartId} options={navigatorChartOptions} series={navigatorSeries} type="area" height="100%" width="100%" />
      </div>
    </div>
  );
};

// =================================================================================
// АЛГОРИТМ УПРОЩЕНИЯ ГРАФИКА LTTB
// =================================================================================
function downsampleLTTB(data, threshold) {
    const dataLength = data.length;
    if (threshold >= dataLength || threshold === 0) return data;
    
    const sampled = []; 
    let sampledIndex = 0;
    const every = (dataLength - 2) / (threshold - 2);
    let a = 0, maxAreaPoint, maxArea, area, nextA;
    
    sampled[sampledIndex++] = data[a];
    
    for (let i = 0; i < threshold - 2; i++) {
        let avgX = 0, avgY = 0;
        let avgRangeStart = Math.floor((i + 1) * every) + 1;
        let avgRangeEnd = Math.floor((i + 2) * every) + 1;
        avgRangeEnd = avgRangeEnd < dataLength ? avgRangeEnd : dataLength;
        
        const avgRangeLength = avgRangeEnd - avgRangeStart;
        
        for (; avgRangeStart < avgRangeEnd; avgRangeStart++) {
            avgX += data[avgRangeStart][0];
            avgY += data[avgRangeStart][1];
        }
        avgX /= avgRangeLength; 
        avgY /= avgRangeLength;
        
        let rangeOffs = Math.floor(i * every) + 1;
        let rangeTo = Math.floor((i + 1) * every) + 1;
        
        const pointAX = data[a][0];
        const pointAY = data[a][1];
        
        maxArea = -1;
        
        for (; rangeOffs < rangeTo; rangeOffs++) {
            area = Math.abs((pointAX - avgX) * (data[rangeOffs][1] - pointAY) - (pointAX - data[rangeOffs][0]) * (avgY - pointAY)) * 0.5;
            if (area > maxArea) {
                maxArea = area;
                maxAreaPoint = data[rangeOffs];
                nextA = rangeOffs;
            }
        }
        
        sampled[sampledIndex++] = maxAreaPoint;
        a = nextA;
    }
    
    sampled[sampledIndex++] = data[dataLength - 1];
    
    return sampled;
}

export default StrategyChartComponent;
